import React from 'react';
import _ from 'underscore';

import hideable from 'sharedreact/components/hideable';
import ParameterSet from 'sharedreact/components/ParameterSet';
import TextField from 'sharedreact/components/TextField';
import type User from 'sharedreact/data/User';
import { translate } from 'sharedreact/utils/LanguageSupport';
import Dialog from 'sharedreact/components/Dialog';
import ValidationForm, { ValidationProps, ValidationWrapper } from 'sharedreact/components/ValidationForm';

type Props = {
  show: boolean,
  language: string,
  activeUser: User | null,
  requestFinished: boolean,
  requestResult: boolean,

  onShow: () => void,
  onClose: () => void,
  onChangePassword: (currentPassword: string, newPassword: string) => void,
} & ValidationProps;

type State = {
  currentPassword: string,
  newPassword: string,
  newPasswordConfirm: string
};

const defaultState: State = {
  currentPassword: '',
  newPassword: '',
  newPasswordConfirm: ''
};

class ChangePasswordDialogImpl extends React.Component<Props, State> {
  private _currentPasswordInput: HTMLInputElement | null;

  constructor (props: Props) {
    super(props);

    this._currentPasswordInput = null;

    this.state = defaultState;

    this.handleRegistration = this.handleRegistration.bind(this);
    this.handleChangeValue = this.handleChangeValue.bind(this);
    this.handleShow = this.handleShow.bind(this);
    this.handleClose = this.handleClose.bind(this);
    this.renderSuccessMessage = this.renderSuccessMessage.bind(this);
    this.renderForm = this.renderForm.bind(this);
  }

  handleShow (): void {
    this.props.onShow();
  }

  focusInput () {
    if (this._currentPasswordInput != null) {
      this._currentPasswordInput.focus();
    }
  }

  handleClose (): void {
    this.setState(defaultState);
    this.props.onClose();
  }

  handleRegistration (): void {
    // Pass the modalId so it can be used to manually handle click dismissal of the dialog.
    this.focusInput();
  }

  handleChangeValue (name: string, value: string, event?: React.ChangeEvent<HTMLInputElement>): void {
    switch (name) {
      case 'currentPassword': {
        this.setState({ [name]: value });
        break;
      }
      case 'newPassword': {
        this.setState({ [name]: value });
        break;
      }
      case 'newPasswordConfirm': {
        this.setState({ [name]: value });
        break;
      }
    }
  }

  handleChangePassword (): void {
    if (this.props.isFormValid) {
      this.props.onChangePassword(this.state.currentPassword, this.state.newPassword);
    }
  }

  override render (): React.ReactNode {
    const {
      requestFinished,
      requestResult,
    } = this.props;

    if (requestFinished && requestResult) {
      return this.renderSuccessMessage();
    } else {
      return this.renderForm();
    }
  }

  renderSuccessMessage (): React.ReactNode {
    const { show, language } = this.props;


    return (
      <Dialog
        id='ae_dialog_newPassword'
        show={show}
        language={language}
        title={translate('shared.changepassworddialog.title')}
        onShown={this.handleRegistration}
        onClose={this.handleClose}
      >
        {translate('shared.changepassworddialog.success.info.passwordchangedsuccessfully')}
      </Dialog>
    );
  }

  renderForm (): React.ReactNode {
    const {
      show,
      activeUser,
      language
    } = this.props;

    const changePasswordDisabled: boolean = !this.state.currentPassword || !this.state.newPassword || !this.state.newPasswordConfirm;
    const userName: string = activeUser ? activeUser.name : '';

    const changePassword = (event?: React.MouseEvent<HTMLButtonElement>) => {
      event?.preventDefault();
      this.handleChangePassword();
    };

    return (
      <Dialog
        id='ae_dialog_newPassword'
        show={show}
        language={language}
        title={translate('shared.changepassworddialog.title')}
        onShown={this.handleRegistration}
        onClose={this.handleClose}
        onConfirm={changePassword}
        disableConfirm={changePasswordDisabled}
        closeText={translate('shared.general.cancel')}
      >
        <ValidationForm>
          <ParameterSet>
            <div style={{display: 'none'}}> { /* hidden username field to please the browser */ }
              <TextField id='ae_input_changepassword_username'
                name='userName'
                label=''
                value={userName}
                autoComplete='userName'
              />
            </div>
            <TextField id='ae_input_changepassword_currentpassword'
              name='currentPassword'
              label={translate('shared.changepassworddialog.currentpassword')}
              value={this.state.currentPassword}
              type='password'
              autoComplete='current-password'
              onChangeValue={this.handleChangeValue}
              validations={['required']}
              inputRef={(inputRef) => { this._currentPasswordInput = inputRef; }}
              validationTooltipPlacement='bottom'
            />
            <div style={{'minHeight': '30px'}} />
            <TextField id='ae_input_changepassword_password'
              name='password'
              label={translate('shared.changepassworddialog.newpassword')}
              value={this.state.newPassword}
              type='password'
              autoComplete='new-password'
              validations={['required', 'password']}
              onChangeValue={(name: string, value: string, event?: React.ChangeEvent<HTMLInputElement>) => this.handleChangeValue('newPassword', value, event)}
              validationTooltipPlacement='bottom'
            />
            <TextField id='ae_input_changepassword_password_confirm'
              name='passwordConfirm'
              label={translate('shared.changepassworddialog.newpasswordconfirm')}
              value={this.state.newPasswordConfirm}
              type='password'
              autoComplete='new-password'
              validations={['required', 'passwordConfirm']}
              onChangeValue={(name: string, value: string, event?: React.ChangeEvent<HTMLInputElement>) => this.handleChangeValue('newPasswordConfirm', value, event)}
              validationTooltipPlacement='bottom'
            />
          </ParameterSet>
        </ValidationForm>
      </Dialog>
    );
  }
}

const ChangePasswordDialog = ValidationWrapper(ChangePasswordDialogImpl);

export default hideable(ChangePasswordDialog);
